/** Vue Router configuration (Sprint 00).
 *
 * Routes are split 1:1 with the 10 pages in the IA doc.
 * Auth guard:
 *   - ``public`` pages (only ``/login``) bypass auth check.
 *   - everything else requires admin token + admin role.
 *   - the auth store is loaded before navigation by calling
 *     ``refresh()`` on first navigation if a token exists.
 *
 * No flash: the guard resolves BEFORE ``<router-view>`` renders the
 * child route.
 */
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/pages/common/ForbiddenPage.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/ConsoleLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: '', redirect: '/mission-control' },
      {
        path: 'overview',
        name: 'overview',
        component: () => import('@/pages/overview/OverviewPage.vue'),
      },
      {
        path: 'user-management',
        alias: 'users',
        name: 'users',
        component: () => import('@/pages/users/UsersPage.vue'),
      },
      {
        path: 'user-management/:id',
        alias: 'users/:id',
        name: 'user-detail',
        component: () => import('@/pages/users/UserDetailPage.vue'),
        props: true,
      },
      {
        path: 'workspaces',
        name: 'workspaces',
        component: () => import('@/pages/workspaces/WorkspacesPage.vue'),
      },
      {
        path: 'conversations',
        name: 'conversations',
        component: () => import('@/pages/conversations/ConversationsListPage.vue'),
      },
      {
        path: 'conversations/:id',
        name: 'conversation-detail',
        component: () => import('@/pages/conversations/ConversationDetailPage.vue'),
        props: true,
      },
      {
        path: 'tasks-runs',
        name: 'tasks-runs',
        component: () => import('@/pages/tasks/TasksRunsPage.vue'),
      },
      {
        path: 'files',
        name: 'files',
        component: () => import('@/pages/files/FilesPage.vue'),
      },
      {
        path: 'artifacts',
        name: 'artifacts',
        component: () => import('@/pages/artifacts/ArtifactsPage.vue'),
      },
      {
        // Console Artifact Observability Closure (artifact-observability-p1):
        // read-only detail page. NO editing / version mgmt / download.
        path: 'artifacts/:artifactId',
        name: 'artifact-detail',
        component: () => import('@/pages/artifacts/ArtifactDetailPage.vue'),
        props: true,
      },
      {
        path: 'runtime-events',
        name: 'runtime-events',
        component: () => import('@/pages/runtime/RuntimeEventsPage.vue'),
      },
      {
        path: 'system-health',
        name: 'system-health',
        component: () => import('@/pages/system/SystemHealthPage.vue'),
      },
      {
        // C11 — Runtime Contract / Harness Bridge (read-only, dev / local / internal only).
        // Hidden in Production: route still mounts but page itself guards via import.meta.env.PROD.
        path: 'runtime-contract',
        name: 'runtime-contract',
        component: () => import('@/pages/runtime-contract/RuntimeContractPage.vue'),
        meta: { devOnly: true },
      },
      {
        // C12 — AI Capabilities (Expert / Skill Management).
        // Admin Control Plane, all envs. Same auth as other admin pages.
        path: 'capabilities',
        name: 'capabilities',
        component: () => import('@/pages/capabilities/CapabilitiesPage.vue'),
      },
      {
        // Console AI Runtime Control Plane (Sprint console-ai-runtime-v1).
        // 4 sections + 5 actions, all backed by live API endpoints.
        path: 'ai-runtime',
        name: 'ai-runtime',
        component: () => import('@/pages/ai-runtime/RuntimeSummaryPage.vue'),
      },
      {
        path: 'commercial',
        name: 'commercial-operations',
        component: () => import('@/pages/commercial/CommercialOperationsPage.vue'),
        meta: { title: '商业运营' },
      },
      {
        // Mission Control — Admin Console Agent Operations V1 Sprint 1.
        // First-paint landing for admins; 5s auto-refresh; 4 widgets
        // (System Status / Active Runs / Recent Issues / Activity Timeline).
        path: 'mission-control',
        name: 'mission-control',
        component: () => import('@/pages/mission-control/MissionControlPage.vue'),
        meta: { title: '驾驶舱' },
      },
      {
        // Agent Ops V2 — Conversation Trace (Surface 1).
        path: 'operations/conversations/:id',
        name: 'operations-conversation-trace',
        component: () => import('@/pages/operations/ConversationTracePage.vue'),
        props: true,
        meta: { title: 'Runtime Trace' },
      },
      {
        // Agent Ops V2 — Context Inspector (Surface 2).
        path: 'operations/context/:runId',
        name: 'operations-context-inspector',
        component: () => import('@/pages/operations/ContextInspectorPage.vue'),
        props: true,
        meta: { title: 'Context Inspector' },
      },
      {
        // Agent Ops V2 — Artifact Management (Surface 4).
        path: 'operations/artifacts',
        name: 'operations-artifacts',
        component: () => import('@/pages/operations/ArtifactManagementPage.vue'),
        meta: { title: 'Artifact Management' },
      },
      {
        // Agent Ops V2 — File Lifecycle (Surface 3).
        path: 'operations/files/:id',
        name: 'operations-file-lifecycle',
        component: () => import('@/pages/operations/FileLifecyclePage.vue'),
        props: true,
        meta: { title: 'File Lifecycle' },
      },
      {
        // Agent Ops V2 — Capability Runtime (Surface 5).
        path: 'operations/capabilities',
        name: 'operations-capabilities',
        component: () => import('@/pages/operations/CapabilityRuntimePage.vue'),
        meta: { title: 'Capability Runtime' },
      },
      {
        // V4 — AI 运营中心。
        path: 'operations/runtime',
        name: 'operations-runtime',
        component: () => import('@/pages/operations/RuntimeOperationsPage.vue'),
        meta: { title: 'AI 运营中心' },
      },
      {
        // V4 — 决策可解释性。
        path: 'operations/runtime/:run_id',
        name: 'operations-runtime-decision',
        component: () => import('@/pages/operations/RuntimeDecisionExplainPage.vue'),
        meta: { title: 'AI 执行过程' },
        props: true,
      },
      {
        // V3 Operator Layer — Capability Management overview.
        path: 'operator/capabilities',
        name: 'operator-capabilities',
        component: () => import('@/pages/operator/OperatorCapabilityOverview.vue'),
        meta: { title: 'Operator · 能力管理' },
      },
      {
        // V3 Operator Layer — Knowledge placeholder (no admin API yet).
        path: 'operator/capabilities/knowledge',
        name: 'operator-knowledge',
        component: () => import('@/pages/operator/OperatorKnowledgePlaceholder.vue'),
        meta: { title: 'Operator · 知识库' },
      },
      {
        // V3.1 Capability Studio V1 — AI 能力工作室首页。
        path: 'operator/capabilities/studio',
        name: 'studio-capability-overview',
        component: () => import('@/pages/operator/StudioCapabilityOverview.vue'),
        meta: { title: 'Capability Studio · 能力工作室' },
      },
      {
        // V3.1 Capability Detail — 单个能力详情。
        path: 'operator/capabilities/:id',
        name: 'studio-capability-detail',
        component: () => import('@/pages/operator/StudioCapabilityDetail.vue'),
        meta: { title: 'Capability Detail' },
        props: true,
      },
      {
        // V3.1 Expert Builder — 7 步 AI 员工创建向导。
        path: 'operator/experts/create',
        name: 'studio-expert-create',
        component: () => import('@/pages/operator/StudioExpertCreate.vue'),
        meta: { title: 'Expert Builder' },
      },
      {
        // V3.1 Skill Builder — Skill 详情(3 段)。
        path: 'operator/skills/:id',
        name: 'studio-skill-detail',
        component: () => import('@/pages/operator/StudioSkillDetail.vue'),
        meta: { title: 'Skill Detail' },
        props: true,
      },
      {
        // V3.1 Knowledge Center — 知识资产管理。
        path: 'operator/knowledge',
        name: 'studio-knowledge-center',
        component: () => import('@/pages/operator/StudioKnowledgeCenter.vue'),
        meta: { title: 'Knowledge Center' },
      },
      {
        // V3.1 Evaluation Center — 能力评估中心。
        path: 'operator/evaluation',
        name: 'studio-evaluation-center',
        component: () => import('@/pages/operator/StudioEvaluationCenter.vue'),
        meta: { title: 'Evaluation Center' },
      },
      {
        // V3.1 Publication Center — 能力生命周期发布管理。
        path: 'operator/publication',
        name: 'studio-publication-center',
        component: () => import('@/pages/operator/StudioPublicationCenter.vue'),
        meta: { title: 'Publication Center' },
      },
      {
        // V6.0/V6.1 Optimization Center — Skill-first AI 能力优化中心
        path: 'operator/optimization',
        name: 'studio-optimization-center',
        component: () => import('@/pages/operator/OptimizationCenter.vue'),
        meta: { title: 'Optimization Center' },
      },
      {
        // V6.0/V6.1 Optimization Detail — Skill 维度详情
        path: 'operator/optimization/:skillId',
        name: 'studio-optimization-detail',
        component: () => import('@/pages/operator/OptimizationDetailPage.vue'),
        props: true,
        meta: { title: 'Optimization Detail' },
      },
      {
        // C13 — Intelligence Overview (read-only aggregate).
        path: 'c13',
        name: 'c13-overview',
        component: () => import('@/pages/c13/OverviewPage.vue'),
      },
      {
        path: 'c13/experts',
        name: 'c13-experts',
        component: () => import('@/pages/c13/ExpertListPage.vue'),
      },
      {
        path: 'c13/experts/:expertId',
        name: 'c13-expert-detail',
        component: () => import('@/pages/c13/ExpertDetailPage.vue'),
        props: true,
      },
      {
        path: 'c13/skills',
        name: 'c13-skills',
        component: () => import('@/pages/c13/SkillListPage.vue'),
      },
      {
        path: 'c13/skills/:skillCode',
        name: 'c13-skill-detail',
        component: () => import('@/pages/c13/SkillDetailPage.vue'),
        props: true,
      },
      {
        path: 'c13/publication',
        name: 'c13-publication',
        component: () => import('@/pages/c13/PublicationGatePage.vue'),
      },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/common/NotFoundPage.vue'),
      },
    ],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to: RouteLocationNormalized) => {
  const auth = useAuthStore();
  if (to.meta.public) return true;
  if (to.meta.devOnly && import.meta.env.PROD) {
    // C11 is hidden in production builds (Vite `mode === 'production'`).
    return { path: '/forbidden' };
  }
  if (!auth.isAuthenticated) {
    return { path: '/login', query: { next: to.fullPath } };
  }
  // Refresh the principal on first navigation so /me is consulted once.
  if (!auth.principal) {
    try {
      await auth.refresh();
    } catch {
      return { path: '/login', query: { next: to.fullPath } };
    }
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { path: '/forbidden' };
  }
  return true;
});
